import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import { HssAssessment, HssBillLine, HssSelection } from '../../types/api';
import { ChasBadge } from './ChasBadge';

type HssPanelProps = {
  visitULID: string;
  /** REF_Diagnosis codes ticked but not saved yet (preview). Null = saved diagnoses. */
  diagnosisCodes?: string[] | null;
  selection: HssSelection | null;
  onSelectionChange: (selection: HssSelection) => void;
  readOnly?: boolean;
  /** Bump to reload (e.g. after Save Dispense). */
  refreshKey?: number;
  /**
   * When this assessment says the patient cannot claim, turn Apply off so Finish is not blocked.
   * Only the panel that matches the diagnoses about to be saved should set this. The dispense
   * panel and the diagnosis dialog share one selection.
   */
  syncIneligibleApply?: boolean;
};

const money = (n: number | null | undefined) => `$${(n ?? 0).toFixed(2)}`;

/**
 * Haze Subsidy Scheme panel (mirrors Julius's Haze button on the CHAS bill screen).
 * Eligibility, condition picker with the 14-day tele limit per condition, Table 2 charges and the approved-medicine note.
 */
export function HssPanel({ visitULID, diagnosisCodes, selection, onSelectionChange, readOnly, refreshKey, syncIneligibleApply }: HssPanelProps) {
  const [data, setData] = useState<HssAssessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshingChas, setRefreshingChas] = useState(false);

  const diagKey = diagnosisCodes ? diagnosisCodes.join(',') : '';
  const condKey = selection?.conditions ? selection.conditions.join(',') : '';

  useEffect(() => {
    setData(null);
  }, [visitULID]);

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiService.getHssAssessment(
          visitULID,
          selection?.conditions ?? null,
          diagnosisCodes ?? null,
        );
        if (!cancelled) {
          setData(res);
          // A leftover "Apply" from an earlier diagnosis must not block Finish once this patient cannot claim.
          if (syncIneligibleApply && !res.eligible && !res.applied && selection?.apply) {
            onSelectionChange({ apply: false, conditions: selection.conditions ?? null });
          }
        }
      } catch {
        // Keep the last assessment. A failed refresh must not hide the box for a patient who can claim.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitULID, diagKey, condKey, refreshKey]);

  const apply = selection?.apply ?? data?.defaultApply ?? false;
  const selectedCodes = useMemo(
    () => (data ? data.conditions.filter((c) => c.selected).map((c) => c.code) : []),
    [data],
  );

  if (!data) return null;
  if (!data.featureEnabled) return null;
  // Citizenship, SSD/CSGF, clinic, card/age, and the 14-day cap are decided on the server.
  // The box is only for a patient who can claim, or a claim already saved on this visit.
  if (!data.eligible && !data.applied) return null;

  const noHaze = data.conditions.length === 0;
  const reasons = [...data.invalidReasons, ...data.patientInvalidReasons];
  const toggleDisabled = readOnly || !data.offerable;
  const showBreakdown = apply && data.eligible && data.offerable;

  const setApply = (next: boolean) => onSelectionChange({ apply: next, conditions: selection?.conditions ?? null });
  const toggleCondition = (code: string, checked: boolean) => {
    const base = selection?.conditions ?? selectedCodes;
    const next = checked ? Array.from(new Set([...base, code])) : base.filter((c) => c !== code);
    onSelectionChange({ apply, conditions: next });
  };

  const refreshChas = async () => {
    setRefreshingChas(true);
    try {
      const chas = await apiService.refreshChas(visitULID);
      setData((prev) => (prev ? { ...prev, chas } : prev));
    } catch {
      /* badge refresh is best effort */
    } finally {
      setRefreshingChas(false);
    }
  };

  return (
    <div style={boxStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>Haze Subsidy Scheme (HSS)</strong>
        <ChasBadge chas={data.chas} size="sm" showText onRefresh={readOnly ? undefined : refreshChas} refreshing={refreshingChas} />
      </div>

      {noHaze ? (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
          No haze diagnosis on this visit (J209/J069/H109, J459/J449/J304).
        </div>
      ) : (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13, cursor: toggleDisabled ? 'not-allowed' : 'pointer' }}>
            <input
              type="checkbox"
              checked={apply && data.offerable}
              disabled={toggleDisabled}
              onChange={(e) => setApply(e.target.checked)}
            />
            <span>Apply HSS (Haze)</span>
            {data.applied ? <span style={{ fontSize: 11, color: '#047857' }}>· saved</span> : null}
            {loading ? <span style={{ fontSize: 11, color: '#94a3b8' }}>· updating…</span> : null}
          </label>

          {reasons.length > 0 && (
            <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, color: '#b91c1c' }}>
              {reasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
          )}

          {apply && data.offerable && !data.eligible && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
              Finish is blocked while Apply HSS is on. Turn it off to finish without HSS.
            </div>
          )}

          <div style={{ marginTop: 8 }}>
            {data.conditions.map((c) => (
              <div key={c.code} style={{ marginBottom: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={c.selected && !c.limitReached}
                    disabled={readOnly || c.limitReached || !apply}
                    onChange={(e) => toggleCondition(c.code, e.target.checked)}
                  />
                  <span style={{ fontWeight: 600 }}>{c.name}</span>
                  <span style={{ color: '#64748b' }}>{c.code} · {c.isChronic ? 'Chronic' : 'Acute'}</span>
                </label>
                {c.message ? (
                  <div style={{ fontSize: 11, marginLeft: 22, color: c.limitReached ? '#b91c1c' : '#475569' }}>{c.message}</div>
                ) : null}
              </div>
            ))}
            {data.complicationCodes.length > 0 && (
              <div style={{ fontSize: 11, color: '#475569' }}>Complications claimed with HSS: {data.complicationCodes.join(', ')}</div>
            )}
          </div>

          {showBreakdown && (
            <div style={{ marginTop: 8, fontSize: 12, borderTop: '1px dashed #cbd5e1', paddingTop: 6 }}>
              <Row label={`Type`} value={`${data.type ?? '-'} · ${data.patientType ?? '-'}`} />
              <Row label="Patient pays (HSS)" value={money(data.patientOop)} />
              <Row label="HSS claim (incl. GST)" value={money(data.claimAmount)} />
              {data.bill && (
                <>
                  <Lines title="Covered by HSS" lines={data.bill.eligibleLines} />
                  <Lines title="Not covered (patient pays)" lines={data.bill.nonClaimableLines} />
                  {data.bill.waivedLines.length > 0 && (
                    <Lines title="Waived ($0, not charged to HSS)" lines={data.bill.waivedLines} strike />
                  )}
                  <Row label="Govt Subsidy (HSS)" value={money(data.bill.hssSubsidy)} />
                  {data.bill.clinicWriteOff > 0 && <Row label="HSS write-off (clinic)" value={money(data.bill.clinicWriteOff)} />}
                  <Row label="Patient total for this visit" value={money(data.bill.patientPayable)} bold />
                  {data.bill.refundDue > 0.05 && data.bill.surplusToAccount && (
                    <div style={{ marginTop: 4, color: '#166534', background: '#dcfce7', padding: '4px 6px', borderRadius: 6 }}>
                      Prepaid more than the HSS share: {money(data.bill.accountCredit ?? data.bill.refundDue)} is kept as account credit
                      (Account tender) at checkout. No refund is made.
                    </div>
                  )}
                  {data.bill.refundDue > 0.05 && !data.bill.surplusToAccount && (
                    <div style={{ marginTop: 4, color: '#92400e', background: '#fef3c7', padding: '4px 6px', borderRadius: 6 }}>
                      Prepaid more than the HSS share: refund {money(data.bill.refundDue)} due. Checkout is held for staff (refund handled manually).
                    </div>
                  )}
                  {data.bill.extraDue > 0.05 && (
                    <div style={{ marginTop: 4, color: '#334155' }}>Patient pays {money(data.bill.extraDue)} more on the portal.</div>
                  )}
                </>
              )}
            </div>
          )}

          <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>{data.approvedMedicinesNote}</div>
          {data.notes.map((n) => (
            <div key={n} style={{ fontSize: 11, color: '#64748b' }}>{n}</div>
          ))}
        </>
      )}
    </div>
  );
}

const boxStyle: React.CSSProperties = {
  border: '1px solid #bae6fd',
  background: '#f0f9ff',
  borderRadius: 8,
  padding: '8px 10px',
  margin: '8px 0',
  maxHeight: '38vh',
  overflowY: 'auto',
  textAlign: 'left',
};

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: bold ? 700 : 400 }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function Lines({ title, lines, strike }: { title: string; lines: HssBillLine[]; strike?: boolean }) {
  if (!lines || lines.length === 0) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ color: '#475569', fontWeight: 600 }}>{title}</div>
      {lines.map((l, i) => (
        <div key={`${l.itemCode ?? l.name}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 8 }}>
          <span>{l.name}</span>
          <span style={{ textDecoration: strike ? 'line-through' : undefined }}>{money(l.amount)}</span>
        </div>
      ))}
    </div>
  );
}
